import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Which nav item should read as current.
 *
 * Two cases:
 *  - On a standalone route (/all-news, /all-events, ...) the answer comes from
 *    the path. The header used to keep whatever the visitor last clicked, so
 *    "Home" stayed highlighted on the news listing.
 *  - On the landing page it follows the scroll position, so the highlight moves
 *    from section to section as the visitor reads down the page.
 *
 * @param {string[]} sectionIds  ids in the order they appear in the document
 * @param {Object}   routeMap    pathname -> section id, for standalone pages
 * @param {number}   headerOffset  height of the fixed header, in px
 */
export default function useActiveSection(sectionIds, routeMap = {}, headerOffset = 80) {
  const { pathname, hash } = useLocation();
  const isLanding = pathname === "/";

  const [scrolled, setScrolled] = useState(sectionIds[0]);
  const visibleRef = useRef(new Set());

  // Stable key so the effect does not re-run on a new array identity.
  const idsKey = sectionIds.join("|");

  useEffect(() => {
    if (!isLanding) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const ids = idsKey.split("|");
    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;

    visibleRef.current = new Set();

    const pick = () => {
      // Bottom of the page: the last section can be too short to reach the
      // band, so credit it explicitly rather than leaving the previous one lit.
      // Only when the page actually scrolls - on a short page the arithmetic
      // is trivially true and would pin the highlight to the last section.
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight > window.innerHeight + 4;
      const atBottom =
        scrollable && window.innerHeight + window.scrollY >= doc.scrollHeight - 2;
      if (atBottom) {
        setScrolled(ids[ids.length - 1]);
        return;
      }
      const current = ids.find((id) => visibleRef.current.has(id));
      if (current) setScrolled(current);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleRef.current.add(entry.target.id);
          else visibleRef.current.delete(entry.target.id);
        });
        pick();
      },
      {
        // A band from just under the fixed header down to mid-viewport. A
        // section counts as current while it occupies that band.
        rootMargin: `-${headerOffset}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    nodes.forEach((node) => observer.observe(node));
    window.addEventListener("scroll", pick, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", pick);
    };
  }, [isLanding, idsKey, headerOffset]);

  return useMemo(() => {
    if (!isLanding) return routeMap[pathname] ?? null;
    // A fresh /#about link should light that item before any scrolling happens.
    if (hash) {
      const fromHash = hash.replace("#", "");
      if (idsKey.split("|").includes(fromHash)) return fromHash;
    }
    return scrolled;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLanding, pathname, hash, scrolled, idsKey]);
}
